import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { conteoFisicoService, ConteoFisico } from '../../services/conteoFisicoService';
import { sucursalService } from '../../services/sucursalService';
import { useAuthStore } from '../../store/authStore';
import ConteoFisicoForm from '../../components/ConteosFisicos/ConteoFisicoForm';

const ConteosFisicosList = () => {
  const [sucursalFilter, setSucursalFilter] = useState<number | undefined>(undefined);
  const [estadoFilter, setEstadoFilter] = useState<string | undefined>(undefined);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const { data, isLoading, error } = useQuery(
    ['conteos-fisicos', sucursalFilter, estadoFilter],
    () => conteoFisicoService.getAll(sucursalFilter, estadoFilter)
  );

  const cancelarMutation = useMutation(
    (id: number) => conteoFisicoService.cancelar(id),
    {
      onSuccess: () => {
        toast.success('Conteo cancelado correctamente');
        queryClient.invalidateQueries(['conteos-fisicos']);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al cancelar conteo');
      }
    }
  );

  const handleCreate = () => {
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
  };

  const handleView = (id: number) => {
    navigate(`/conteos-fisicos/${id}`);
  };

  const handleCancel = (id: number) => {
    if (window.confirm('¿Estás seguro de cancelar este conteo?')) {
      cancelarMutation.mutate(id);
    }
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'pendiente':
        return 'bg-gray-100 text-gray-800';
      case 'en_proceso':
        return 'bg-blue-100 text-blue-800';
      case 'finalizado':
        return 'bg-green-100 text-green-800';
      case 'cancelado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstadoLabel = (estado: string) => {
    switch (estado) {
      case 'pendiente':
        return 'Pendiente';
      case 'en_proceso':
        return 'En Proceso';
      case 'finalizado':
        return 'Finalizado';
      case 'cancelado':
        return 'Cancelado';
      default:
        return estado;
    }
  };

  if (isLoading) return <div>Cargando conteos físicos...</div>;
  if (error) return <div>Error al cargar conteos físicos</div>;

  const conteos = data?.data.conteos || [];
  const sucursales = sucursalesData?.data.sucursales || [];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Conteos Físicos</h1>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
        >
          + Nuevo Conteo
        </button>
      </div>

      <div className="mb-4 flex space-x-4">
        <select
          value={sucursalFilter || ''}
          onChange={(e) => setSucursalFilter(e.target.value ? Number(e.target.value) : undefined)}
          className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Todas las sucursales</option>
          {sucursales.map((sucursal) => (
            <option key={sucursal.id} value={sucursal.id}>
              {sucursal.name}
            </option>
          ))}
        </select>

        <select
          value={estadoFilter || ''}
          onChange={(e) => setEstadoFilter(e.target.value || undefined)}
          className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="en_proceso">En Proceso</option>
          <option value="finalizado">Finalizado</option>
          <option value="cancelado">Cancelado</option>
        </select>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sucursal
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usuario
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fecha Inicio
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fecha Fin
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {conteos.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  No hay conteos físicos registrados
                </td>
              </tr>
            ) : (
              conteos.map((conteo) => (
                <tr key={conteo.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{conteo.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {conteo.sucursal?.name || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {conteo.usuario?.name || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getEstadoColor(conteo.estado)}`}>
                      {getEstadoLabel(conteo.estado)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {conteo.fechaInicio
                      ? new Date(conteo.fechaInicio).toLocaleString('es-MX')
                      : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {conteo.fechaFin
                      ? new Date(conteo.fechaFin).toLocaleString('es-MX')
                      : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleView(conteo.id)}
                      className="text-primary-600 hover:text-primary-900 mr-4"
                    >
                      Ver
                    </button>
                    {conteo.estado !== 'finalizado' && conteo.estado !== 'cancelado' && (
                      <button
                        onClick={() => handleCancel(conteo.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Cancelar
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showForm && <ConteoFisicoForm onClose={handleClose} />}
    </div>
  );
};

export default ConteosFisicosList;

